import React, { useContext } from 'react'
import styled from 'styled-components'
import Modal from '@jarvis.oddle/core/components/Modal'
import { H2, Text } from '@jarvis.oddle/core/components/Typo'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import { makeControlledModal } from 'src/decorators/ModalController'
import { paddingSizes } from '@jarvis.oddle/core/theme/default'
import breakpoint from 'src/utils/styledComponentsBreakpoint'
import Form from '@jarvis.oddle/core/components/Form'
import IconClose from '@jarvis.oddle/core/components/Icons/IconClose'
import Flex from '@jarvis.oddle/core/components/Flex'
import Div from '@jarvis.oddle/core/components/Div'
import IconOddleLogistic from 'src/components/icons/IconOddleLogistic'

const WrapperForm = styled(Div).attrs(() => ({
  width: '100%',
  as: Form
}))`
  display: contents;
`

const Container = styled(Modal.FlexContainer).attrs(() => ({
  width: '100%',
  height: '100%',
  flexDirection: 'column',
  padding: paddingSizes.medium
}))`
  position: relative;
  background: ${(props) => props.theme.colorContainerColor};
  width: 95%;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.2);
  border-radius: 5px;
  transform: translateX(-50%) translateY(-50%);
  top: 50%;
  left: 50%;
  height: fit-content;
  ${breakpoint('desktop')`
    width: 100%;
    max-width: 600px;
    height: 100%;
    max-height: 287px;
  `}
`

const StyledModalBody = styled(Modal.Body)`
  color: ${(props) => props.theme.colorBodyFont};
  background-color: transparent;
  text-align: center;
  overflow: hidden;
`

const StyledModalHeaders = styled(Modal.Header)`
  color: ${(props) => props.theme.colorHeadingsFont};
  background-color: transparent;
`

const StyledFormSubmitButton = styled(Form.SubmitButton)`
  width: 100%;
  color: ${(props) => props.theme.buttonTextColor};
  background-color: ${(props) => props.theme.buttonColor};
`

const StyledModalFooter = styled(Modal.Footer)<{ isOddleLogistic: boolean }>`
  box-shadow: none;
  ${(props) =>
    !props.isOddleLogistic &&
    `
    margin-bottom: -15px;
  `}
  ${(props) =>
    props.isOddleLogistic &&
    `
      display: flex;
      flex-direction: column;
      align-items: center;
  `}
`

const CloseButtonContainer = styled(Flex).attrs(() => ({
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  role: 'button'
}))`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 40px;
  height: 40px;
  cursor: pointer;
  z-index: 10;
`

const StyledOddleLogistic = styled(Div)`
  margin-top: 10px;
  margin-bottom: 10px;
`

const StyledHandledDelivery = styled(H2)`
  color: ${props => props.theme.colorHeadingsFont};
`

interface DeliveryPartyModalProps {
  modal: {
    show: boolean
    onClose: () => void
  }
  deliveryParty: string
}

const DeliveryPartyModal = ({
  modal,
  deliveryParty
}: DeliveryPartyModalProps): JSX.Element => {
  const { t } = useContext(TranslationContext)
  const handlingFormSubmit = () => {
    const urlHelp =
      deliveryParty === 'Oddle Logistics'
        ? 'https://help.oddle.me/en/articles/5639104-for-customers-how-will-my-order-be-delivered#h_d92410b28e'
        : 'https://help.oddle.me/en/articles/5639104-for-customers-how-will-my-order-be-delivered#h_627c190c20'
    window.open(urlHelp)
  }
  return (
    <Modal {...modal} show={modal.show} slideInAnimation='right'>
      <WrapperForm
        onSubmit={(values, { setSubmitting }) => {
          setSubmitting(true)
          handlingFormSubmit()
          setSubmitting(false)
        }}
      >
        <Container>
          <CloseButtonContainer onClick={modal.onClose}>
            <IconClose width={14} height={14} />
          </CloseButtonContainer>
          <StyledModalHeaders boxShadow='none'>
            <StyledHandledDelivery bold normalised>
              {deliveryParty === 'Oddle Logistics'
                ? t('Managed by Oddle Logistics')
                : t('Delivered by Restaurant')}
            </StyledHandledDelivery>
          </StyledModalHeaders>
          <StyledModalBody>
            <Text>
              {deliveryParty === 'Oddle Logistics'
                ? t(
                    'Oddle Logistics is a team of real people coordinating with multiple food delivery partners to ensure that your delivery is in good hands.'
                  )
                : t(
                    'This restaurant will be using their own delivery partners, which means that you may have to contact the restaurant for additional information about your delivery.'
                  )}
            </Text>
          </StyledModalBody>
          <StyledModalFooter
            isOddleLogistic={deliveryParty === 'Oddle Logistics'}
          >
            <StyledFormSubmitButton>{t('Learn More')}</StyledFormSubmitButton>
            {deliveryParty === 'Oddle Logistics' && (
              <StyledOddleLogistic>
                <IconOddleLogistic width={86} height={13} />
              </StyledOddleLogistic>
            )}
          </StyledModalFooter>
        </Container>
      </WrapperForm>
    </Modal>
  )
}

export default makeControlledModal(DeliveryPartyModal, 'DeliveryPartyModal')
